import { Money } from '../components/ui/Money';
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { toast } from '../hooks/use-toast';
import {
  Plus, Search, UserPlus, ClipboardCheck, GraduationCap, CalendarClock,
  ChevronRight, Trash2, Edit, ArrowRight, Users, ListChecks, Clock, Check, ChevronsUpDown,
  FileUp, FileText, Download, Package, X, UserCheck, BarChart3, AlertTriangle, TrendingUp, Building2
} from 'lucide-react';
import axios from 'axios';
import { OfferLetterLink, JoinCountdown } from '../components/onboarding/OfferLink';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';

const API = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = ['FOH','Bar','Kitchen','Finance','Human Resource','IT','Operations','Marketing','Admin'];
const DESIGNATIONS = [];
const TOP_NATIONALITIES = ['Indian', 'Filipino', 'Nepali', 'Pakistani'];
const ALL_NATIONALITIES = [
  'Afghan','Albanian','Algerian','American','Andorran','Angolan','Argentine','Armenian',
  'Australian','Austrian','Azerbaijani','Bahamian','Bahraini','Bangladeshi','Barbadian',
  'Belarusian','Belgian','Belizean','Beninese','Bhutanese','Bolivian','Bosnian','Brazilian',
  'British','Bruneian','Bulgarian','Burkinabe','Burundian','Cambodian','Cameroonian',
  'Canadian','Cape Verdean','Central African','Chadian','Chilean','Chinese','Colombian',
  'Comorian','Congolese','Costa Rican','Croatian','Cuban','Cypriot','Czech','Danish',
  'Djiboutian','Dominican','Dutch','East Timorese','Ecuadorean','Egyptian','Emirati',
  'Equatorial Guinean','Eritrean','Estonian','Ethiopian','Fijian','Filipino','Finnish',
  'French','Gabonese','Gambian','Georgian','German','Ghanaian','Greek','Grenadian',
  'Guatemalan','Guinean','Guyanese','Haitian','Honduran','Hungarian','Icelandic','Indian',
  'Indonesian','Iranian','Iraqi','Irish','Israeli','Italian','Ivorian','Jamaican','Japanese',
  'Jordanian','Kazakh','Kenyan','Korean','Kuwaiti','Kyrgyz','Laotian','Latvian','Lebanese',
  'Liberian','Libyan','Lithuanian','Luxembourgish','Macedonian','Malagasy','Malawian',
  'Malaysian','Maldivian','Malian','Maltese','Mauritanian','Mauritian','Mexican','Moldovan',
  'Mongolian','Montenegrin','Moroccan','Mozambican','Myanmar','Namibian','Nepalese','Nepali',
  'New Zealander','Nicaraguan','Nigerian','Norwegian','Omani','Pakistani','Panamanian',
  'Paraguayan','Peruvian','Polish','Portuguese','Qatari','Romanian','Russian','Rwandan',
  'Saudi','Senegalese','Serbian','Sierra Leonean','Singaporean','Slovak','Slovenian',
  'Somali','South African','Spanish','Sri Lankan','Sudanese','Surinamese','Swedish','Swiss',
  'Syrian','Taiwanese','Tajik','Tanzanian','Thai','Togolese','Trinidadian','Tunisian',
  'Turkish','Turkmen','Ugandan','Ukrainian','Uruguayan','Uzbek','Venezuelan','Vietnamese',
  'Yemeni','Zambian','Zimbabwean'
];

const STAGE_COLORS = {
  "Offer Sent": "bg-blue-100 text-blue-800 border-blue-300",
  "Offer Accepted": "bg-purple-100 text-purple-800 border-purple-300",
  "Visa Under Process": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "Ready to Join": "bg-green-100 text-green-800 border-green-300",
  "Onboarded": "bg-emerald-100 text-emerald-800 border-emerald-300",
};

const STAGE_BG = {
  "Offer Sent": "border-l-blue-500",
  "Offer Accepted": "border-l-purple-500",
  "Visa Under Process": "border-l-yellow-500",
  "Ready to Join": "border-l-green-500",
  "Onboarded": "border-l-emerald-500",
};

export default function Onboarding() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer' || user.role === 'report_viewer';

  const [records, setRecords] = useState([]);
  const [stages, setStages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCompany, setFilterCompany] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ task: '', responsible: 'HR', deadline: '' });
  const [showAddTraining, setShowAddTraining] = useState(false);
  const [newTraining, setNewTraining] = useState({ training_name: '', trainer: '', training_date: '', status: 'Pending' });
  const [natOpen, setNatOpen] = useState(false);
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [newPosition, setNewPosition] = useState('');
  const [positions, setPositions] = useState(DESIGNATIONS);
  useEffect(() => { axios.get(`${API}/api/positions`).then(r => setPositions(r.data || [])).catch(() => {}); }, []);
  // Document states
  const [docType, setDocType] = useState('Passport Copy');
  const [docNotes, setDocNotes] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  // Asset allocation states
  const [companyAssets, setCompanyAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [assetNotes, setAssetNotes] = useState('');
  // Convert to Employee states
  const [showConvert, setShowConvert] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertForm, setConvertForm] = useState({
    name: '', email: '', phone: '', date_of_birth: '', gender: 'Male',
    nationality: '', position: '', department: '', company: '', join_date: '',
    employee_role: 'Staff', payment_method: 'WPS',
    salary: { basic: 0, housing: 0, transportation: 0, other: 0 },
  });
  // Dashboard states
  const [activeTab, setActiveTab] = useState('pipeline');
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  const emptyForm = {
    candidate_name: '', position: '', department: '', company: '',
    contact_number: '', email: '', nationality: '', expected_join_date: '',
    reporting_manager: '', status: 'Offer Sent', notes: ''
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [recRes, stgRes, compRes, dtRes, caRes] = await Promise.all([
        axios.get(`${API}/api/onboarding/`),
        axios.get(`${API}/api/onboarding/stages`),
        axios.get(`${API}/api/company-assets/companies`),
        axios.get(`${API}/api/onboarding/document-types`),
        axios.get(`${API}/api/company-assets`),
      ]);
      setRecords(recRes.data);
      setStages(stgRes.data);
      setCompanies(compRes.data);
      setDocTypes(dtRes.data);
      setCompanyAssets(caRes.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const filteredRecords = useMemo(() => {
    return records.filter(r => {
      const matchSearch = !search ||
        r.candidate_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.position?.toLowerCase().includes(search.toLowerCase()) ||
        r.department?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || r.status === filterStatus;
      const matchComp = filterCompany === 'all' || r.company === filterCompany;
      return matchSearch && matchStatus && matchComp;
    });
  }, [records, search, filterStatus, filterCompany]);

  const pipelineCounts = useMemo(() => {
    const counts = {};
    stages.forEach(s => { counts[s] = 0; });
    records.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });
    return counts;
  }, [records, stages]);

  const openAdd = () => { setEditingRecord(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (rec) => {
    setEditingRecord(rec);
    setForm({
      candidate_name: rec.candidate_name || '', position: rec.position || '',
      department: rec.department || '', company: rec.company || '',
      contact_number: rec.contact_number || '', email: rec.email || '',
      nationality: rec.nationality || '', expected_join_date: rec.expected_join_date || '',
      reporting_manager: rec.reporting_manager || '', status: rec.status || 'Offer Sent',
      notes: rec.notes || ''
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.candidate_name) {
      toast({ title: 'Error', description: 'Candidate name is required', variant: 'destructive' });
      return;
    }
    try {
      if (editingRecord) {
        await axios.put(`${API}/api/onboarding/${editingRecord.id}`, form);
        toast({ title: 'Success', description: 'Record updated' });
        if (selectedRecord?.id === editingRecord.id) {
          const res = await axios.get(`${API}/api/onboarding/${editingRecord.id}`);
          setSelectedRecord(res.data);
        }
      } else {
        await axios.post(`${API}/api/onboarding/`, form);
        toast({ title: 'Success', description: 'Onboarding record created' });
      }
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/onboarding/${deleteConfirm.id}`);
      toast({ title: 'Deleted' });
      if (selectedRecord?.id === deleteConfirm.id) setSelectedRecord(null);
      loadAll();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setDeleting(false); setDeleteConfirm(null); }
  };

  const handleStatusChange = async (rec, newStatus) => {
    try {
      await axios.put(`${API}/api/onboarding/${rec.id}/status`, { status: newStatus });
      toast({ title: 'Status Updated', description: `Moved to ${newStatus}` });
      loadAll();
      if (selectedRecord?.id === rec.id) {
        setSelectedRecord(prev => ({ ...prev, status: newStatus }));
      }
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const toggleChecklist = async (itemId, completed) => {
    if (!selectedRecord) return;
    try {
      await axios.put(`${API}/api/onboarding/${selectedRecord.id}/checklist/${itemId}`, { completed });
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const addChecklistItem = async () => {
    if (!selectedRecord || !newTask.task) return;
    try {
      await axios.post(`${API}/api/onboarding/${selectedRecord.id}/checklist`, newTask);
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
      setNewTask({ task: '', responsible: 'HR', deadline: '' });
      setShowAddTask(false);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const deleteChecklistItem = async (itemId) => {
    if (!selectedRecord) return;
    try {
      await axios.delete(`${API}/api/onboarding/${selectedRecord.id}/checklist/${itemId}`);
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const addTraining = async () => {
    if (!selectedRecord || !newTraining.training_name) return;
    try {
      await axios.post(`${API}/api/onboarding/${selectedRecord.id}/trainings`, newTraining);
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
      setNewTraining({ training_name: '', trainer: '', training_date: '', status: 'Pending' });
      setShowAddTraining(false);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const toggleReview = async (reviewId, completed) => {
    if (!selectedRecord) return;
    try {
      await axios.put(`${API}/api/onboarding/${selectedRecord.id}/reviews/${reviewId}`, { completed });
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  // Document handlers
  const handleUploadDoc = async () => {
    if (!selectedRecord || !docFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', docFile);
      fd.append('document_type', docType);
      fd.append('notes', docNotes);
      await axios.post(`${API}/api/onboarding/${selectedRecord.id}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'Success', description: 'Document uploaded' });
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
      setDocFile(null);
      setDocNotes('');
      setDocType('Passport Copy');
    } catch { toast({ title: 'Error', description: 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!selectedRecord) return;
    try {
      await axios.delete(`${API}/api/onboarding/${selectedRecord.id}/documents/${docId}`);
      toast({ title: 'Deleted' });
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleDownloadDoc = async (doc) => {
    if (!selectedRecord) return;
    try {
      const res = await axios.get(
        `${API}/api/onboarding/${selectedRecord.id}/documents/${doc.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast({ title: 'Error', description: 'Download failed', variant: 'destructive' }); }
  };

  // Asset allocation handlers
  const handleAllocateAsset = async () => {
    if (!selectedRecord || !selectedAssetId) return;
    const asset = companyAssets.find(a => a.id === selectedAssetId);
    if (!asset) return;
    try {
      await axios.post(`${API}/api/onboarding/${selectedRecord.id}/assets`, {
        asset_id: asset.id,
        asset_name: asset.asset_name,
        asset_code: asset.asset_code,
        notes: assetNotes,
      });
      toast({ title: 'Success', description: `${asset.asset_name} allocated` });
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
      setSelectedAssetId('');
      setAssetNotes('');
      // Refresh company assets to update assignments
      const caRes = await axios.get(`${API}/api/company-assets`);
      setCompanyAssets(caRes.data);
    } catch { toast({ title: 'Error', description: 'Allocation failed', variant: 'destructive' }); }
  };

  const handleRemoveAllocation = async (allocationId) => {
    if (!selectedRecord) return;
    try {
      await axios.delete(`${API}/api/onboarding/${selectedRecord.id}/assets/${allocationId}`);
      toast({ title: 'Removed' });
      const res = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(res.data);
      const caRes = await axios.get(`${API}/api/company-assets`);
      setCompanyAssets(caRes.data);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const openDetail = async (rec) => {
    try {
      const res = await axios.get(`${API}/api/onboarding/${rec.id}`);
      setSelectedRecord(res.data);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  // Convert to Employee
  const openConvertDialog = (rec) => {
    setConvertForm({
      name: rec.candidate_name || '',
      email: rec.email || '',
      phone: rec.contact_number || '',
      date_of_birth: '',
      gender: 'Male',
      nationality: rec.nationality || '',
      position: rec.position || '',
      department: rec.department || '',
      company: rec.company || '',
      join_date: rec.expected_join_date || new Date().toISOString().split('T')[0],
      employee_role: 'Staff',
      payment_method: 'WPS',
      salary: { basic: 0, housing: 0, transportation: 0, other: 0 },
    });
    setShowConvert(true);
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    if (!convertForm.email) {
      toast({ title: 'Error', description: 'Email is required', variant: 'destructive' });
      return;
    }
    if (!convertForm.date_of_birth) {
      toast({ title: 'Error', description: 'Date of birth is required', variant: 'destructive' });
      return;
    }
    setConverting(true);
    try {
      const res = await axios.post(`${API}/api/onboarding/${selectedRecord.id}/convert-to-employee`, convertForm);
      toast({ title: 'Employee Created', description: res.data.message });
      setShowConvert(false);
      // Refresh the record
      const updated = await axios.get(`${API}/api/onboarding/${selectedRecord.id}`);
      setSelectedRecord(updated.data);
      loadAll();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to convert', variant: 'destructive' });
    } finally {
      setConverting(false);
    }
  };

  // Dashboard
  const loadDashboard = async () => {
    setLoadingDashboard(true);
    try {
      const res = await axios.get(`${API}/api/onboarding/stats/dashboard`);
      setDashboardData(res.data);
    } catch { /* ignore */ }
    finally { setLoadingDashboard(false); }
  };

  const checklistProgress = (checklist) => {
    if (!checklist?.length) return 0;
    return Math.round((checklist.filter(c => c.completed).length / checklist.length) * 100);
  };

  if (selectedRecord) {
    const rec = selectedRecord;
    const progress = checklistProgress(rec.checklist);
    const currentIdx = stages.indexOf(rec.status);
    const nextStage = currentIdx < stages.length - 1 ? stages[currentIdx + 1] : null;

    return (
      <div className="space-y-6" data-testid="onboarding-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedRecord(null)} data-testid="back-to-list-btn">
            &larr; Back
          </Button>
          <h1 className="text-2xl font-bold">{rec.candidate_name}</h1>
          <Badge className={STAGE_COLORS[rec.status]}>{rec.status}</Badge>
          {nextStage && !isViewer && (
            <Button size="sm" variant="outline" onClick={() => handleStatusChange(rec, nextStage)}
              data-testid="advance-status-btn">
              Move to {nextStage} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {!isViewer && !rec.linked_employee_id && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => openConvertDialog(rec)}
              data-testid="convert-to-employee-btn">
              <UserCheck className="h-4 w-4 mr-1" /> Convert to Employee
            </Button>
          )}
          {rec.linked_employee_id && (
            <Badge className="bg-green-100 text-green-800 border-green-300">
              <UserCheck className="h-3 w-3 mr-1" /> Employee {rec.linked_employee_code || 'Created'}
            </Badge>
          )}
          {!isViewer && (
            <Button size="sm" variant="ghost" onClick={() => openEdit(rec)} data-testid="edit-onboarding-btn">
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="pt-3 pb-2">
            <p className="text-xs text-gray-500">Position</p>
            <p className="font-medium text-sm">{rec.position || 'N/A'}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-3 pb-2">
            <p className="text-xs text-gray-500">Department</p>
            <p className="font-medium text-sm">{rec.department || 'N/A'}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-3 pb-2">
            <p className="text-xs text-gray-500">Company</p>
            <p className="font-medium text-sm">{rec.company || 'N/A'}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-3 pb-2">
            <p className="text-xs text-gray-500">Expected Join Date</p>
            <p className="font-medium text-sm">{rec.expected_join_date || 'N/A'}</p>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <JoinCountdown rec={rec} />
              <OfferLetterLink offerId={rec.offer_letter_id} testId="detail-open-offer-letter" />
            </div>
          </CardContent></Card>
        </div>

        {/* Progress bar */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">Onboarding Progress</span>
            <span className="text-sm text-gray-500">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div className="bg-green-500 h-2.5 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="checklist" className="w-full">
          <TabsList className="w-full grid grid-cols-5">
            <TabsTrigger value="checklist" className="flex items-center gap-1">
              <ClipboardCheck className="h-4 w-4" /> Checklist
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1" data-testid="documents-tab">
              <FileText className="h-4 w-4" /> Documents
            </TabsTrigger>
            <TabsTrigger value="assets" className="flex items-center gap-1" data-testid="assets-tab">
              <Package className="h-4 w-4" /> Assets
            </TabsTrigger>
            <TabsTrigger value="training" className="flex items-center gap-1">
              <GraduationCap className="h-4 w-4" /> Training
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center gap-1">
              <CalendarClock className="h-4 w-4" /> Reviews
            </TabsTrigger>
          </TabsList>

          {/* CHECKLIST TAB */}
          <TabsContent value="checklist" className="space-y-3 mt-4">
            {!isViewer && (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowAddTask(true)} data-testid="add-task-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Task
                </Button>
              </div>
            )}
            {showAddTask && (
              <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Task name" value={newTask.task}
                    onChange={e => setNewTask(p => ({ ...p, task: e.target.value }))} data-testid="new-task-input" />
                  <Input placeholder="Responsible" value={newTask.responsible}
                    onChange={e => setNewTask(p => ({ ...p, responsible: e.target.value }))} />
                  <Input type="date" value={newTask.deadline}
                    onChange={e => setNewTask(p => ({ ...p, deadline: e.target.value }))} />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => setShowAddTask(false)}>Cancel</Button>
                  <Button size="sm" onClick={addChecklistItem} data-testid="save-task-btn">Add</Button>
                </div>
              </div>
            )}
            <div className="space-y-1">
              {(rec.checklist || []).map(item => (
                <div key={item.id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${item.completed ? 'bg-green-50 border-green-200' : 'bg-white'}`}
                  data-testid={`checklist-item-${item.id}`}>
                  <Checkbox checked={item.completed} disabled={isViewer}
                    onCheckedChange={v => toggleChecklist(item.id, v)}
                    data-testid={`check-${item.id}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${item.completed ? 'line-through text-gray-400' : ''}`}>{item.task}</p>
                    <p className="text-xs text-gray-500">{item.responsible}{item.deadline ? ` | Due: ${item.deadline}` : ''}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{item.category}</Badge>
                  {!isViewer && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                      onClick={() => deleteChecklistItem(item.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-3 mt-4">
            {!isViewer && (
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <p className="text-sm font-medium">Upload Document</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Document Type</Label>
                    <Select value={docType} onValueChange={setDocType}>
                      <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {docTypes.map(dt => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input value={docNotes} onChange={e => setDocNotes(e.target.value)}
                      placeholder="e.g. Front and back" data-testid="doc-notes-input" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex-1 flex items-center gap-2 border-2 border-dashed rounded-lg p-3 cursor-pointer hover:bg-white transition"
                    data-testid="doc-file-input-label">
                    <FileUp className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-500 truncate">
                      {docFile ? docFile.name : 'Choose file to upload...'}
                    </span>
                    <input type="file" className="hidden" onChange={e => setDocFile(e.target.files?.[0] || null)}
                      data-testid="doc-file-input" />
                    {docFile && (
                      <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0 ml-auto"
                        onClick={(e) => { e.preventDefault(); setDocFile(null); }}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </label>
                  <Button size="sm" onClick={handleUploadDoc} disabled={!docFile || uploading}
                    data-testid="upload-doc-btn">
                    <FileUp className="h-4 w-4 mr-1" />
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </div>
            )}
            {(rec.documents || []).length === 0 ? (
              <p className="text-center py-6 text-gray-400 text-sm">No documents uploaded yet</p>
            ) : (
              <div className="space-y-2">
                {(rec.documents || []).map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 border rounded-lg bg-white"
                    data-testid={`doc-${doc.id}`}>
                    <FileText className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{doc.document_type}</p>
                      <p className="text-xs text-gray-500 truncate">{doc.original_filename}</p>
                      {doc.notes && <p className="text-xs text-gray-400 mt-0.5">{doc.notes}</p>}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}
                    </span>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                      onClick={() => handleDownloadDoc(doc)} data-testid={`download-doc-${doc.id}`}>
                      <Download className="h-4 w-4 text-blue-600" />
                    </Button>
                    {!isViewer && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        onClick={() => handleDeleteDoc(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ASSETS TAB */}
          <TabsContent value="assets" className="space-y-3 mt-4">
            {!isViewer && (
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <p className="text-sm font-medium">Allocate Company Asset</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Select Asset</Label>
                    <Select value={selectedAssetId} onValueChange={setSelectedAssetId}>
                      <SelectTrigger data-testid="allocate-asset-select"><SelectValue placeholder="Choose asset..." /></SelectTrigger>
                      <SelectContent>
                        {companyAssets
                          .filter(a => a.assigned_to_type === 'none' || !a.assigned_to_type)
                          .map(a => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.asset_code} - {a.asset_name} {a.owning_company ? `(${a.owning_company})` : ''}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input value={assetNotes} onChange={e => setAssetNotes(e.target.value)}
                      placeholder="e.g. To be collected on first day" data-testid="asset-notes-input" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={handleAllocateAsset} disabled={!selectedAssetId}
                    data-testid="allocate-asset-btn">
                    <Package className="h-4 w-4 mr-1" /> Allocate
                  </Button>
                </div>
              </div>
            )}
            {(rec.allocated_assets || []).length === 0 ? (
              <p className="text-center py-6 text-gray-400 text-sm">No assets allocated yet</p>
            ) : (
              <div className="space-y-2">
                {(rec.allocated_assets || []).map(alloc => (
                  <div key={alloc.id} className="flex items-center gap-3 p-3 border rounded-lg bg-white"
                    data-testid={`alloc-${alloc.id}`}>
                    <Package className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{alloc.asset_name}</p>
                      <p className="text-xs text-gray-500">{alloc.asset_code}</p>
                      {alloc.notes && <p className="text-xs text-gray-400 mt-0.5">{alloc.notes}</p>}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {new Date(alloc.allocated_at).toLocaleDateString('en-GB')}
                    </span>
                    {!isViewer && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        onClick={() => handleRemoveAllocation(alloc.id)} data-testid={`remove-alloc-${alloc.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TRAINING TAB */}
          <TabsContent value="training" className="space-y-3 mt-4">
            {!isViewer && (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowAddTraining(true)} data-testid="add-training-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Training
                </Button>
              </div>
            )}
            {showAddTraining && (
              <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Training name" value={newTraining.training_name}
                    onChange={e => setNewTraining(p => ({ ...p, training_name: e.target.value }))} data-testid="training-name-input" />
                  <Input placeholder="Trainer" value={newTraining.trainer}
                    onChange={e => setNewTraining(p => ({ ...p, trainer: e.target.value }))} />
                  <Input type="date" value={newTraining.training_date}
                    onChange={e => setNewTraining(p => ({ ...p, training_date: e.target.value }))} />
                  <Select value={newTraining.status} onValueChange={v => setNewTraining(p => ({ ...p, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Skipped">Skipped</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => setShowAddTraining(false)}>Cancel</Button>
                  <Button size="sm" onClick={addTraining} data-testid="save-training-btn">Add</Button>
                </div>
              </div>
            )}
            {(rec.trainings || []).length === 0 ? (
              <p className="text-center py-6 text-gray-400 text-sm">No training records yet</p>
            ) : (
              <div className="space-y-2">
                {(rec.trainings || []).map(t => (
                  <div key={t.id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                    <div>
                      <p className="text-sm font-medium">{t.training_name}</p>
                      <p className="text-xs text-gray-500">{t.trainer}{t.training_date ? ` | ${t.training_date}` : ''}</p>
                    </div>
                    <Badge className={
                      t.status === 'Completed' ? 'bg-green-100 text-green-700' :
                      t.status === 'Skipped' ? 'bg-gray-100 text-gray-600' :
                      'bg-yellow-100 text-yellow-700'
                    }>{t.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* REVIEWS TAB */}
          <TabsContent value="reviews" className="space-y-3 mt-4">
            {(rec.review_reminders || []).length === 0 ? (
              <p className="text-center py-6 text-gray-400 text-sm">No review reminders — set an expected join date to auto-generate</p>
            ) : (
              <div className="space-y-2">
                {(rec.review_reminders || []).map(r => (
                  <div key={r.id} className={`flex items-center gap-3 p-3 border rounded-lg ${r.completed ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                    <Checkbox checked={r.completed} disabled={isViewer}
                      onCheckedChange={v => toggleReview(r.id, v)} />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${r.completed ? 'line-through text-gray-400' : ''}`}>{r.type}</p>
                      <p className="text-xs text-gray-500">Due: {r.due_date}</p>
                    </div>
                    <Badge className={r.completed ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                      {r.completed ? 'Done' : 'Pending'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Convert to Employee Dialog */}
        <Dialog open={showConvert} onOpenChange={setShowConvert}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-green-600" />
                Convert to Employee
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleConvert} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={convertForm.name} onChange={e => setConvertForm(p => ({ ...p, name: e.target.value }))} required data-testid="convert-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={convertForm.email} onChange={e => setConvertForm(p => ({ ...p, email: e.target.value }))} required data-testid="convert-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={convertForm.phone} onChange={e => setConvertForm(p => ({ ...p, phone: e.target.value }))} data-testid="convert-phone" />
                </div>
                <div>
                  <Label>Date of Birth *</Label>
                  <Input type="date" value={convertForm.date_of_birth} onChange={e => setConvertForm(p => ({ ...p, date_of_birth: e.target.value }))} required data-testid="convert-dob" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={convertForm.gender} onValueChange={v => setConvertForm(p => ({ ...p, gender: v }))}>
                    <SelectTrigger data-testid="convert-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nationality</Label>
                  <Input value={convertForm.nationality} onChange={e => setConvertForm(p => ({ ...p, nationality: e.target.value }))} data-testid="convert-nationality" />
                </div>
                <div>
                  <Label>Position</Label>
                  <Input value={convertForm.position} onChange={e => setConvertForm(p => ({ ...p, position: e.target.value }))} data-testid="convert-position" />
                </div>
                <div>
                  <Label>Department</Label>
                  <Select value={convertForm.department} onValueChange={v => setConvertForm(p => ({ ...p, department: v }))}>
                    <SelectTrigger data-testid="convert-department"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Join Date</Label>
                  <Input type="date" value={convertForm.join_date} onChange={e => setConvertForm(p => ({ ...p, join_date: e.target.value }))} data-testid="convert-join-date" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={convertForm.employee_role} onValueChange={v => setConvertForm(p => ({ ...p, employee_role: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Staff">Staff</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Head of Department">Head of Department</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={convertForm.payment_method} onValueChange={v => setConvertForm(p => ({ ...p, payment_method: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="WPS">WPS</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold mb-2 block">Salary Components (AED)</Label>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Basic</Label>
                    <Input type="number" value={convertForm.salary.basic} onChange={e => setConvertForm(p => ({ ...p, salary: { ...p.salary, basic: parseFloat(e.target.value) || 0 } }))} data-testid="convert-salary-basic" />
                  </div>
                  <div>
                    <Label className="text-xs">Housing</Label>
                    <Input type="number" value={convertForm.salary.housing} onChange={e => setConvertForm(p => ({ ...p, salary: { ...p.salary, housing: parseFloat(e.target.value) || 0 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Transport</Label>
                    <Input type="number" value={convertForm.salary.transportation} onChange={e => setConvertForm(p => ({ ...p, salary: { ...p.salary, transportation: parseFloat(e.target.value) || 0 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Other</Label>
                    <Input type="number" value={convertForm.salary.other} onChange={e => setConvertForm(p => ({ ...p, salary: { ...p.salary, other: parseFloat(e.target.value) || 0 } }))} />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Total: <Money value={convertForm.salary.basic + convertForm.salary.housing + convertForm.salary.transportation + convertForm.salary.other} />
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button type="button" variant="outline" onClick={() => setShowConvert(false)}>Cancel</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={converting} data-testid="confirm-convert-btn">
                  <UserCheck className="h-4 w-4 mr-1" />
                  {converting ? 'Creating...' : 'Create Employee'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---- LIST VIEW ----
  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2"><UserPlus className="h-6 w-6" /> Employee Onboarding</h1>
        <div className="flex gap-2">
          {!isViewer && (
            <Button onClick={openAdd} data-testid="add-onboarding-btn">
              <Plus className="h-4 w-4 mr-1" /> New Onboarding
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={v => { setActiveTab(v); if (v === 'dashboard') loadDashboard(); }}>
        <TabsList>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">
            <ListChecks className="h-4 w-4 mr-1" /> Pipeline
          </TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-1" /> Dashboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="space-y-6 mt-4">

      {/* Pipeline overview */}
      <div className="grid grid-cols-5 gap-3">
        {stages.map((stage, i) => (
          <Card key={stage} className={`cursor-pointer transition hover:shadow-md ${filterStatus === stage ? 'ring-2 ring-blue-400' : ''}`}
            onClick={() => setFilterStatus(filterStatus === stage ? 'all' : stage)}>
            <CardContent className="pt-3 pb-2 text-center">
              <p className="text-2xl font-bold">{pipelineCounts[stage] || 0}</p>
              <p className="text-xs text-gray-500 mt-0.5">{stage}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search candidates..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="search-onboarding" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCompany} onValueChange={setFilterCompany}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Records list */}
      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : (
        <div className="space-y-2">
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No onboarding records found</div>
          ) : filteredRecords.map(rec => {
            const progress = checklistProgress(rec.checklist);
            return (
              <div key={rec.id}
                className={`border rounded-lg p-4 bg-white hover:shadow-md transition cursor-pointer border-l-4 ${STAGE_BG[rec.status] || 'border-l-gray-300'}`}
                onClick={() => openDetail(rec)}
                data-testid={`onboarding-card-${rec.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{rec.candidate_name}</p>
                        <Badge className={`${STAGE_COLORS[rec.status]} text-xs`}>{rec.status}</Badge>
                        <JoinCountdown rec={rec} />
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {[rec.position, rec.department, rec.company].filter(Boolean).join(' | ')}
                      </p>
                      <OfferLetterLink offerId={rec.offer_letter_id} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {rec.expected_join_date && (
                      <span className="text-xs text-gray-400">Join: {rec.expected_join_date}</span>
                    )}
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <div className="w-16 bg-gray-200 rounded-full h-1.5">
                        <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${progress}%` }}></div>
                      </div>
                      <span className="text-xs text-gray-500">{progress}%</span>
                    </div>
                    {!isViewer && (
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(rec)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => setDeleteConfirm(rec)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      </TabsContent>

      {/* Dashboard Tab */}
      <TabsContent value="dashboard" className="mt-4">
        {loadingDashboard ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : dashboardData ? (
          <div className="space-y-6" data-testid="onboarding-dashboard">
            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <Users className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                  <p className="text-2xl font-bold">{dashboardData.total_active}</p>
                  <p className="text-xs text-gray-500">Active</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <UserCheck className="h-5 w-5 mx-auto mb-1 text-green-500" />
                  <p className="text-2xl font-bold">{dashboardData.total_onboarded}</p>
                  <p className="text-xs text-gray-500">Onboarded</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <ListChecks className="h-5 w-5 mx-auto mb-1 text-orange-500" />
                  <p className="text-2xl font-bold">{dashboardData.pending_tasks_count}</p>
                  <p className="text-xs text-gray-500">Pending Tasks</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-red-500" />
                  <p className="text-2xl font-bold">{dashboardData.overdue_tasks}</p>
                  <p className="text-xs text-gray-500">Overdue</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <TrendingUp className="h-5 w-5 mx-auto mb-1 text-purple-500" />
                  <p className="text-2xl font-bold">{dashboardData.avg_days_to_onboard}d</p>
                  <p className="text-xs text-gray-500">Avg. Onboard Time</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Status Breakdown */}
              <Card>
                <CardContent className="pt-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-gray-500" /> Pipeline Breakdown
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(dashboardData.by_status || {}).map(([status, count]) => {
                      const total = dashboardData.total_all || 1;
                      const pct = Math.round((count / total) * 100);
                      return (
                        <div key={status} className="flex items-center gap-3">
                          <span className="text-xs w-32 truncate text-gray-600">{status}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                            <div className={`h-full rounded-full flex items-center justify-end pr-2 text-xs font-medium text-white ${
                              status === 'Onboarded' ? 'bg-green-500' :
                              status === 'Ready to Join' ? 'bg-emerald-400' :
                              status === 'Visa Under Process' ? 'bg-yellow-500' :
                              status === 'Offer Accepted' ? 'bg-purple-500' : 'bg-blue-500'
                            }`} style={{ width: `${Math.max(pct, 8)}%` }}>
                              {count}
                            </div>
                          </div>
                          <span className="text-xs text-gray-400 w-10 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* By Department */}
              <Card>
                <CardContent className="pt-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-500" /> By Department
                  </h3>
                  {Object.keys(dashboardData.by_department || {}).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(dashboardData.by_department || {}).sort((a, b) => b[1] - a[1]).map(([dept, count]) => (
                        <div key={dept} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                          <span className="text-sm text-gray-700">{dept}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No active candidates</p>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming Joins */}
              <Card>
                <CardContent className="pt-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 text-gray-500" /> Upcoming Joins (30 days)
                  </h3>
                  {(dashboardData.upcoming_joins || []).length > 0 ? (
                    <div className="space-y-2">
                      {dashboardData.upcoming_joins.map(j => (
                        <div key={j.id} className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50">
                          <div>
                            <p className="text-sm font-medium">{j.name}</p>
                            <p className="text-xs text-gray-400">{j.position} · {j.department}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-medium">{j.join_date}</p>
                            <Badge className={STAGE_COLORS[j.status]} variant="outline">{j.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No upcoming joins</p>
                  )}
                </CardContent>
              </Card>

              {/* Pending Tasks */}
              <Card>
                <CardContent className="pt-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-gray-500" /> Top Pending Tasks
                  </h3>
                  {(dashboardData.pending_tasks || []).length > 0 ? (
                    <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
                      {dashboardData.pending_tasks.slice(0, 15).map((t, i) => (
                        <div key={i} className={`flex items-center gap-2 p-2 rounded text-sm ${t.overdue ? 'bg-red-50 border border-red-100' : 'bg-gray-50'}`}>
                          {t.overdue && <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <span className="font-medium truncate block">{t.task}</span>
                            <span className="text-xs text-gray-400">{t.candidate} · {t.responsible}</span>
                          </div>
                          {t.deadline && <span className="text-xs text-gray-400 flex-shrink-0">{t.deadline}</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">All tasks completed</p>
                  )}
                </CardContent>
              </Card>

              {/* Overdue Reviews */}
              {(dashboardData.overdue_reviews || []).length > 0 && (
                <Card className="lg:col-span-2">
                  <CardContent className="pt-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-red-600">
                      <AlertTriangle className="h-4 w-4" /> Overdue Reviews
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {dashboardData.overdue_reviews.map((r, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-red-50 border border-red-100 rounded-lg">
                          <div>
                            <p className="text-sm font-medium">{r.candidate}</p>
                            <p className="text-xs text-gray-500">{r.review_type}</p>
                          </div>
                          <span className="text-xs text-red-600 font-medium">Due: {r.due_date}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <BarChart3 className="h-12 w-12 mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">Click the Dashboard tab to load analytics</p>
          </div>
        )}
      </TabsContent>
      </Tabs>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Edit Onboarding' : 'New Onboarding'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Candidate Name *</Label>
                <Input value={form.candidate_name} onChange={e => setForm(p => ({ ...p, candidate_name: e.target.value }))}
                  required data-testid="candidate-name-input" />
              </div>
              <div>
                <Label>Position</Label>
                {!showAddPosition ? (
                  <Select value={form.position || '__none__'} onValueChange={v => {
                    if (v === '__add_new__') { setShowAddPosition(true); }
                    else { setForm(p => ({ ...p, position: v === '__none__' ? '' : v })); }
                  }}>
                    <SelectTrigger data-testid="candidate-position-input"><SelectValue placeholder="Select position" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Select --</SelectItem>
                      {positions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      <SelectItem value="__add_new__" className="text-purple-600 font-medium">+ Add Position</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2">
                    <Input value={newPosition} onChange={e => setNewPosition(e.target.value)}
                      placeholder="New position..." className="flex-1" data-testid="new-position-input" />
                    <Button type="button" size="sm" onClick={() => {
                      if (newPosition.trim()) {
                        const np = newPosition.trim().replace(/\s+/g, ' ').toUpperCase();
                        setPositions(prev => (prev.includes(np) ? prev : [...prev, np]));
                        setForm(p => ({ ...p, position: np }));
                        axios.post(`${API}/api/positions`, { position: np }).catch(() => {});
                        setNewPosition('');
                      }
                      setShowAddPosition(false);
                    }} data-testid="save-position-btn">Add</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => { setShowAddPosition(false); setNewPosition(''); }}>Cancel</Button>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Select value={form.department || '__none__'} onValueChange={v => setForm(p => ({ ...p, department: v === '__none__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- Select --</SelectItem>
                    {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Company</Label>
                <Select value={form.company || '__none__'} onValueChange={v => setForm(p => ({ ...p, company: v === '__none__' ? '' : v }))}>
                  <SelectTrigger data-testid="candidate-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Number</Label>
                <Input value={form.contact_number} onChange={e => setForm(p => ({ ...p, contact_number: e.target.value }))} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nationality</Label>
                <Popover open={natOpen} onOpenChange={setNatOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={natOpen}
                      className="w-full justify-between font-normal"
                      data-testid="candidate-nationality-select">
                      {form.nationality || "Select nationality..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search nationality..." />
                      <CommandList>
                        <CommandEmpty>No nationality found.</CommandEmpty>
                        <CommandGroup heading="Common">
                          {TOP_NATIONALITIES.map(nat => (
                            <CommandItem key={nat} value={nat} onSelect={() => { setForm(p => ({ ...p, nationality: nat })); setNatOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${form.nationality === nat ? 'opacity-100' : 'opacity-0'}`} />
                              {nat}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        <CommandGroup heading="All">
                          {ALL_NATIONALITIES.filter(n => !TOP_NATIONALITIES.includes(n)).map(nat => (
                            <CommandItem key={nat} value={nat} onSelect={() => { setForm(p => ({ ...p, nationality: nat })); setNatOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${form.nationality === nat ? 'opacity-100' : 'opacity-0'}`} />
                              {nat}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Expected Join Date</Label>
                <Input type="date" value={form.expected_join_date}
                  onChange={e => setForm(p => ({ ...p, expected_join_date: e.target.value }))}
                  data-testid="candidate-join-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reporting Manager</Label>
                <Input value={form.reporting_manager} onChange={e => setForm(p => ({ ...p, reporting_manager: e.target.value }))} />
              </div>
              <div>
                <Label>Pre-Joining Status</Label>
                <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="candidate-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-onboarding-btn">{editingRecord ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}
        title="Delete Onboarding Record" description="This will remove the onboarding record and all associated tasks."
        itemName={deleteConfirm?.candidate_name} onConfirm={handleDelete} loading={deleting} />
    </div>
  );
}
